import { useCallback, useEffect, useRef, useState } from 'react';
import { operatorConsole, type PoolOfferConfig } from '../../lib/api/operatorConsole';

export interface PackArtwork {
  packImageUrl: string | null;
  cardImageUrls: string[];
}

const artworkOf = (pack: PoolOfferConfig): PackArtwork => ({
  packImageUrl: pack.pack_image_url,
  cardImageUrls: pack.sample_card_image_urls,
});

// The pack's artwork for the preview, read afresh on open and before a scene remount:
// artwork URLs can be short-lived signed links. Until a read answers, what the caller had.
export function usePackArtwork(pack: PoolOfferConfig) {
  const [artwork, setArtwork] = useState(() => artworkOf(pack));
  const readSeq = useRef(0);
  const packId = pack.pool_offer_id;
  const imageUrl = pack.pack_image_url;

  const reload = useCallback(async () => {
    const seq = ++readSeq.current;
    try {
      const resp = await operatorConsole.listPoolOfferConfigs({ availableOnly: true, includeStaged: true });
      const fresh = resp.pool_offer_configs.find((p) => p.pool_offer_id === packId);
      if (seq === readSeq.current && fresh) setArtwork(artworkOf(fresh));
    } catch {
      // Keep the artwork already shown
    }
  }, [packId]);

  // Again when the caller's pack image changes: an upload that landed after opening
  useEffect(() => {
    void reload();
  }, [reload, imageUrl]);

  return { artwork, reload };
}
